package com.example.event_management_system.ui.screens.events

import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import com.example.event_management_system.data.models.Event
import com.example.event_management_system.data.models.EventStatus
import com.example.event_management_system.data.models.Location
import com.example.event_management_system.data.models.Section
import com.example.event_management_system.data.models.User
import com.example.event_management_system.data.models.UserRole
import com.example.event_management_system.data.repository.IAuthRepository
import com.example.event_management_system.data.repository.IEmailRepository
import com.example.event_management_system.data.repository.IEventRepository
import com.example.event_management_system.data.repository.ILocationRepository
import com.example.event_management_system.data.repository.ISectionRepository
import com.example.event_management_system.data.repository.IUserRepository
import kotlin.time.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import com.example.event_management_system.util.currentLocalDate
import com.example.event_management_system.util.currentTimeMillis
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.datetime.atStartOfDayIn
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class EventFormState(
    val eventId: String? = null,
    val customerName: String = "",
    val customerEmail: String = "",
    val customerPhone: String = "",
    val eventDate: Long = 0L,
    val eventTime: String = "",
    val eventEndTime: String = "",
    val guestCount: String = "",
    val cost: String = "",
    val deposit: String = "",
    val locationId: String = "",
    val sections: List<Section> = emptyList(),
    val sectionId: String? = null,
    val eventPurpose: String = "",
    val catererContact: String = "",
    val notes: String = "",
    val postEventNotes: String = "",
    val status: EventStatus = EventStatus.PENDING,
    val locations: List<Location> = emptyList(),
    val currentUser: User? = null,
    val isLoading: Boolean = true,
    val isSaving: Boolean = false,
    val error: String? = null,
    val validationErrors: Map<String, String> = emptyMap(),
    val isSaved: Boolean = false,
    val savedEventId: String? = null,
    val showEmailPreview: Boolean = false,
    val emailDraftBody: String = "",
    val isSendingEmail: Boolean = false,
    val isEditMode: Boolean = false,
    val isOwner: Boolean = false,
    val showAddLocationDialog: Boolean = false,
    val newLocationName: String = "",
    val emailSentSuccess: Boolean = false
)

class EventFormScreenModel(
    private val eventRepository: IEventRepository,
    private val locationRepository: ILocationRepository,
    private val sectionRepository: ISectionRepository,
    private val userRepository: IUserRepository,
    private val authRepository: IAuthRepository,
    private val emailRepository: IEmailRepository
) : ScreenModel {

    private var sectionsJob: kotlinx.coroutines.Job? = null

    private val _state = MutableStateFlow(EventFormState())
    val state: StateFlow<EventFormState> = _state.asStateFlow()

    fun initialize(eventId: String?, prefilledDate: Long?, initialLocationId: String? = null) {
        val todayUtcMidnight = currentLocalDate().atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds()
        _state.update {
            it.copy(
                eventId = eventId,
                isEditMode = eventId != null,
                eventDate = prefilledDate ?: todayUtcMidnight,
                locationId = initialLocationId ?: it.locationId
            )
        }
        loadData()
    }

    private fun loadData() {
        screenModelScope.launch {
            _state.update { it.copy(isLoading = true) }

            val userId = authRepository.getCurrentUserId()
            if (userId == null) {
                _state.update { it.copy(isLoading = false, error = "Not authenticated") }
                return@launch
            }

            val user = userRepository.getUser(userId)
            if (user == null) {
                _state.update { it.copy(isLoading = false, error = "User not found") }
                return@launch
            }

            _state.update { it.copy(currentUser = user, isOwner = user.role == UserRole.OWNER) }

            // Load locations
            val locationsFlow = if (user.role == UserRole.OWNER) {
                locationRepository.getAllLocationsFlow()
            } else {
                locationRepository.getLocationsByIdsFlow(user.locationIds)
            }

            launch {
                locationsFlow.collect { locations ->
                    _state.update { currentState ->
                        val locationId = currentState.locationId.ifEmpty {
                            locations.firstOrNull()?.id ?: ""
                        }
                        currentState.copy(locations = locations, locationId = locationId)
                    }
                    val resolvedId = _state.value.locationId
                    if (resolvedId.isNotEmpty()) loadSections(resolvedId)
                }
            }

            // Load existing event if editing
            val eventId = _state.value.eventId
            if (eventId != null) {
                val event = eventRepository.getEvent(eventId)
                if (event != null) {
                    _state.update { currentState ->
                        currentState.copy(
                            customerName = event.customerName,
                            customerEmail = event.customerEmail,
                            customerPhone = event.customerPhone,
                            eventDate = event.eventDate,
                            eventTime = event.eventTime,
                            eventEndTime = event.eventEndTime ?: "",
                            guestCount = event.guestCount.toString(),
                            cost = event.cost ?: "",
                            deposit = event.deposit ?: "",
                            locationId = event.locationId,
                            sectionId = event.sectionId,
                            eventPurpose = event.eventPurpose ?: "",
                            catererContact = event.catererContact ?: "",
                            notes = event.notes ?: "",
                            postEventNotes = event.postEventNotes ?: "",
                            status = event.status,
                            isLoading = false
                        )
                    }
                    if (event.locationId.isNotEmpty()) {
                        loadSections(event.locationId)
                    }
                } else {
                    _state.update { it.copy(isLoading = false, error = "Event not found") }
                }
            } else {
                _state.update { it.copy(isLoading = false) }
            }
        }
    }

    fun onCustomerNameChange(value: String) {
        _state.update { 
            it.copy(
                customerName = value, 
                validationErrors = it.validationErrors - "customerName"
            ) 
        }
    }

    fun onCustomerEmailChange(value: String) {
        _state.update { 
            it.copy(
                customerEmail = value, 
                validationErrors = it.validationErrors - "customerEmail"
            ) 
        }
    }

    fun onCustomerPhoneChange(value: String) {
        _state.update { 
            it.copy(
                customerPhone = value, 
                validationErrors = it.validationErrors - "customerPhone"
            ) 
        }
    }

    fun onEventDateChange(value: Long) {
        _state.update { 
            it.copy(
                eventDate = value, 
                validationErrors = it.validationErrors - "eventDate"
            ) 
        }
    }

    fun onEventTimeChange(value: String) {
        _state.update { 
            it.copy(
                eventTime = value, 
                validationErrors = it.validationErrors - "eventTime"
            ) 
        }
    }

    fun onEventEndTimeChange(value: String) {
        _state.update { it.copy(eventEndTime = value) }
    }

    fun onGuestCountChange(value: String) {
        if (value.isEmpty() || value.all { it.isDigit() }) {
            _state.update { 
                it.copy(
                    guestCount = value, 
                    validationErrors = it.validationErrors - "guestCount"
                ) 
            }
        }
    }

    fun onCostChange(value: String) {
        _state.update { it.copy(cost = value) }
    }

    fun onDepositChange(value: String) {
        _state.update { it.copy(deposit = value, validationErrors = it.validationErrors - "deposit") }
    }

    fun onLocationChange(locationId: String) {
        _state.update {
            it.copy(
                locationId = locationId,
                sectionId = null,
                sections = emptyList(),
                validationErrors = it.validationErrors - "locationId"
            )
        }
        loadSections(locationId)
    }

    private fun loadSections(locationId: String) {
        sectionsJob?.cancel()
        sectionsJob = screenModelScope.launch {
            sectionRepository.getSectionsByLocationFlow(locationId).collect { sections ->
                _state.update { it.copy(sections = sections) }
            }
        }
    }

    fun onSectionChange(sectionId: String?) {
        _state.update { it.copy(sectionId = sectionId) }
    }

    fun showAddLocationDialog() {
        _state.update { it.copy(showAddLocationDialog = true, newLocationName = "") }
    }

    fun hideAddLocationDialog() {
        _state.update { it.copy(showAddLocationDialog = false, newLocationName = "") }
    }

    fun onNewLocationNameChange(value: String) {
        _state.update { it.copy(newLocationName = value) }
    }

    fun addLocation() {
        val name = _state.value.newLocationName.trim()
        if (name.isBlank()) return
        screenModelScope.launch {
            try {
                val ownerId = authRepository.getCurrentUserId() ?: ""
                val location = Location(name = name, ownerId = ownerId, isActive = true)
                val newId = locationRepository.createLocation(location)
                val saved = location.copy(id = newId)
                _state.update { it.copy(
                    locations = it.locations + saved,
                    locationId = newId,
                    showAddLocationDialog = false,
                    newLocationName = ""
                ) }
            } catch (e: Throwable) {
                _state.update { it.copy(error = "Failed to add location: ${e.message}") }
            }
        }
    }

    fun onEventPurposeChange(value: String) {
        _state.update { it.copy(eventPurpose = value) }
    }

    fun onCatererContactChange(value: String) {
        _state.update { it.copy(catererContact = value) }
    }

    fun onNotesChange(value: String) {
        _state.update { it.copy(notes = value) }
    }

    fun onPostEventNotesChange(value: String) {
        _state.update { it.copy(postEventNotes = value) }
    }

    fun onStatusChange(status: EventStatus) {
        _state.update { it.copy(status = status) }
    }

    private fun validate(): Boolean {
        val s = _state.value
        val errors = mutableMapOf<String, String>()

        if (s.customerName.isBlank())
            errors["customerName"] = "Name is required"

        when {
            s.customerEmail.isBlank() -> errors["customerEmail"] = "Email is required"
            !isValidEmail(s.customerEmail) -> errors["customerEmail"] = "Enter a valid email address"
        }

        when {
            s.customerPhone.isBlank() -> errors["customerPhone"] = "Phone number is required"
            s.customerPhone.filter { it.isDigit() }.length < 10 ->
                errors["customerPhone"] = "Enter a valid phone number (min 10 digits)"
        }

        if (s.eventDate <= 0L)
            errors["eventDate"] = "Date is required"

        if (s.eventTime.isBlank())
            errors["eventTime"] = "Start time is required"

        when {
            s.guestCount.isBlank() -> errors["guestCount"] = "Guest count is required"
            (s.guestCount.toIntOrNull() ?: 0) <= 0 -> errors["guestCount"] = "Enter a valid number of guests"
        }

        if (s.deposit.isBlank())
            errors["deposit"] = "Deposit is required"

        _state.update { it.copy(validationErrors = errors) }
        return errors.isEmpty()
    }

    private fun isValidEmail(email: String): Boolean {
        return email.contains("@") && email.contains(".")
    }

    private fun isValidTimeFormat(time: String): Boolean {
        val regex = Regex("^(1[0-2]|0?[1-9]):[0-5][0-9]\\s?(AM|PM|am|pm)$")
        return regex.matches(time.trim())
    }

    fun save() {
        if (!validate()) return

        screenModelScope.launch {
            _state.update { it.copy(isSaving = true, error = null) }

            try {
                val currentState = _state.value
                val location = currentState.locations.find { it.id == currentState.locationId }
                val userId = authRepository.getCurrentUserId() ?: ""

                val selectedSection = currentState.sections.find { it.id == currentState.sectionId }
                val event = Event(
                    id = currentState.eventId ?: "",
                    customerName = currentState.customerName.trim(),
                    customerEmail = currentState.customerEmail.trim(),
                    customerPhone = currentState.customerPhone.trim(),
                    eventDate = currentState.eventDate,
                    eventTime = currentState.eventTime.trim(),
                    eventEndTime = currentState.eventEndTime.trim().ifEmpty { null },
                    guestCount = currentState.guestCount.toIntOrNull() ?: 0,
                    cost = currentState.cost.trim().ifEmpty { null },
                    deposit = currentState.deposit.trim().ifEmpty { null },
                    locationId = currentState.locationId,
                    locationName = location?.name ?: "",
                    sectionId = currentState.sectionId,
                    sectionName = selectedSection?.name,
                    eventPurpose = currentState.eventPurpose.trim().ifEmpty { null },
                    catererContact = currentState.catererContact.trim().ifEmpty { null },
                    notes = currentState.notes.trim().ifEmpty { null },
                    postEventNotes = currentState.postEventNotes.trim().ifEmpty { null },
                    status = currentState.status,
                    createdBy = userId
                )

                if (currentState.isEditMode && currentState.eventId != null) {
                    eventRepository.updateEvent(currentState.eventId, event)
                    val savedEvent = event.copy(id = currentState.eventId)
                    _state.update { it.copy(isSaving = false, savedEventId = currentState.eventId) }
                    if (savedEvent.customerEmail.isNotBlank()) showEmailPreviewAfterSave(savedEvent)
                    else _state.update { it.copy(isSaved = true) }
                } else {
                    val newId = eventRepository.createEvent(event)
                    val savedEvent = event.copy(id = newId)
                    _state.update { it.copy(isSaving = false, savedEventId = newId) }
                    if (savedEvent.customerEmail.isNotBlank()) showEmailPreviewAfterSave(savedEvent)
                    else _state.update { it.copy(isSaved = true) }
                }
            } catch (e: Throwable) {
                _state.update { 
                    it.copy(
                        isSaving = false, 
                        error = e.message ?: "Failed to save event"
                    ) 
                }
            }
        }
    }

    fun clearError() {
        _state.update { it.copy(error = null) }
    }

    fun confirmSendEmail() {
        val eventId = _state.value.savedEventId ?: run {
            _state.update { it.copy(isSaved = true) }
            return
        }
        val body = _state.value.emailDraftBody
        _state.update { it.copy(showEmailPreview = false, isSendingEmail = true) }
        screenModelScope.launch {
            val result = emailRepository.sendEventConfirmation(eventId, bodyOverride = body)
            _state.update {
                it.copy(
                    isSendingEmail = false,
                    emailSentSuccess = result.isSuccess,
                    error = if (result.isSuccess) null
                    else result.exceptionOrNull()?.message ?: "Failed to send confirmation email."
                )
            }
        }
    }

    fun skipEmail() {
        _state.update { it.copy(showEmailPreview = false, isSaved = true) }
    }

    private fun showEmailPreviewAfterSave(event: Event) {
        val s = _state.value
        val location = s.locations.find { it.id == event.locationId }
        val senderName = s.currentUser?.displayName ?: ""
        val template = location?.emailBodyTemplate?.takeIf { it.isNotBlank() } ?: DEFAULT_EMAIL_TEMPLATE
        val roleStr = s.currentUser?.role?.name
        val staffPhone = s.currentUser?.phone
        val locName = location?.name ?: event.locationName.ifBlank { null }
        val signature = if (!location?.emailSignatureLine.isNullOrBlank())
            applySignatureTemplate(location!!.emailSignatureLine!!, senderName, roleStr, staffPhone, locName, location.address, location.phone)
            else buildSignature(senderName, staffPhone, locName)
        val draft = applyEmailTemplate(template, event, signature)
        _state.update { it.copy(showEmailPreview = true, emailDraftBody = draft) }
    }

    private fun buildSignature(
        senderName: String,
        staffPhone: String?,
        locationName: String?
    ): String = listOfNotNull(
        senderName.ifBlank { null },
        staffPhone?.ifBlank { null },
        locationName?.ifBlank { null }
    ).joinToString("\n")

    private fun applySignatureTemplate(
        template: String,
        senderName: String,
        userRole: String?,
        staffPhone: String?,
        locationName: String?,
        locationAddress: String?,
        locationPhone: String?
    ): String = template
        .replace("{{staffName}}", senderName)
        .replace("{{staffRole}}", userRole?.lowercase()?.replaceFirstChar { it.uppercase() } ?: "")
        .replace("{{staffPhone}}", staffPhone ?: "")
        .replace("{{locationName}}", locationName ?: "")
        .replace("{{locationAddress}}", locationAddress ?: "")
        .replace("{{locationPhone}}", locationPhone ?: "")
        .replace("{{logo}}", "%%LOGOIMG%%")

    private fun applyEmailTemplate(
        template: String,
        event: Event,
        signature: String
    ): String {
        val locationName = event.locationName.ifBlank { "" }
        val date = formatEmailDate(event.eventDate)
        val timeRange = listOfNotNull(
            event.eventTime.ifBlank { null },
            event.eventEndTime?.ifBlank { null }
        ).joinToString(" - ")
        val notesBlock = if (!event.notes.isNullOrBlank()) "Notes:\n${event.notes}" else ""
        val purposeBlock = if (!event.eventPurpose.isNullOrBlank()) "Purpose: ${event.eventPurpose}" else ""

        return template
            .replace("{{name}}", event.customerName.ifBlank { "there" })
            .replace("{{location}}", locationName)
            .replace("{{date}}", date)
            .replace("{{time}}", timeRange)
            .replace("{{guests}}", event.guestCount.toString())
            .replace("{{cost}}", event.cost?.let { "\$$it" } ?: "")
            .replace("{{deposit}}", event.deposit?.let { "\$$it" } ?: "")
            .replace("{{status}}", event.status.name.lowercase().replaceFirstChar { it.uppercase() })
            .replace("{{notes}}", event.notes ?: "")
            .replace("{{notesBlock}}", notesBlock)
            .replace("{{purpose}}", event.eventPurpose ?: "")
            .replace("{{purposeBlock}}", purposeBlock)
            .replace("{{signature}}", signature)
    }

    private fun formatEmailDate(epochMs: Long): String {
        if (epochMs <= 0L) return ""
        return try {
            val dt = Instant.fromEpochMilliseconds(epochMs).toLocalDateTime(TimeZone.UTC)
            val dow = dt.date.dayOfWeek.name.lowercase().replaceFirstChar { it.uppercase() }
            val month = dt.month.name.lowercase().replaceFirstChar { it.uppercase() }
            "$dow, $month ${dt.dayOfMonth}, ${dt.year}"
        } catch (e: Exception) {
            ""
        }
    }
}

private const val DEFAULT_EMAIL_TEMPLATE = """Hi {{name}},

Please find the confirmation details for your upcoming event at {{location}} below:

Location: {{location}}
Date: {{date}}
Time: {{time}}
Guest Count: {{guests}}
{{purposeBlock}}
Total Cost: {{cost}}
Deposit: {{deposit}}
Status: {{status}}

Thanks again for booking your party at {{location}}, we are here to help you host a great event. Please reply if you have any questions regarding the details above.


See you soon,


{{signature}}"""
